import React, { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';

import StyledInput from '../common/form/styled-input';
import { strings, parameters } from '../../localization/strings';
import StyledLabel from '../common/form/styled-label';
import Heading2 from '../common/headings/heading2';
import StyledError from '../common/form/styled-error';
import CommonAnimatedLoader from '../common/animated/loader';
import CommonButtonDefault from '../common/buttons/default';
import { useSite } from '../../hooks/use-site';
import AlertPrimary from '../common/alerts/alert-primary';
import * as usersService from '../../services/api/users';
import { useAuth } from '../../hooks/use-auth';
import { getLocalizedPath } from '../../helpers/localization-helper';
import * as questionnaireApi from '../../services/api/questionnaire';
import { Country } from '../../entities/country';
import { pushGTMEvent, GTM_EVENTS } from '../../utils/gtm-events';

interface ComponentProps {
  isQuestionnaire?: boolean;
  orderHash?: string;
}

type Inputs = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  countryId: number;
  agreeMarketing: boolean;
  agreeResearch: boolean;
  agreeTerms: boolean;
  agreeRemoteConsultation: boolean;
};

function UsersShipmentFormComponent({ isQuestionnaire }: ComponentProps) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [countries, setCountries] = useState<Country[]>([]);
  const { token, user } = useAuth();

  const { isSelfCo, locale, region } = useSite();

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setIsLoading(true);
        const regionParam = isSelfCo && region ? region : undefined;
        const res = await questionnaireApi.getCountries(regionParam);

        if (res) {
          setCountries(res);
        }
        setIsLoading(false);
      } catch (e) {
        setErrorMessage((e as Error).message);
      }
    };

    fetchCountries();
  }, [isSelfCo, region]);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: {
      address: user?.address || '',
      city: user?.city || '',
      state: user?.state || '',
      zipCode: user?.zipCode || '',
      phone: user?.phone || '',
      countryId: user?.countryId ? user.countryId : undefined,
    },
  });

  useEffect(() => {
    if (countries.length === 1 && !user?.countryId) {
      setValue('countryId', countries[0].id);
    }
  }, [countries, user?.countryId, setValue]);

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    try {
      setIsLoading(true);

      await usersService.updateContact(token, data);

      pushGTMEvent(GTM_EVENTS.ADD_SHIPPING);

      if (isQuestionnaire) {
        navigate(getLocalizedPath(parameters().selectPatient, locale, region));
      } else {
        navigate(getLocalizedPath(parameters().dashboard, locale, region));
      }
    } catch (e) {
      setIsLoading(false);
      if (e instanceof Error) {
        setErrorMessage(strings().error.common.title);
      }
    }
  };

  return isLoading ? (
    <CommonAnimatedLoader />
  ) : (
    <div>
      <Heading2 className="mb-6">{strings().shipment.title}</Heading2>
      <div className="md:w-9/12 mb-6 md:mb-10 my-5">{strings().shipment.subtitle}</div>
      <form onSubmit={handleSubmit(onSubmit)}>
        {errorMessage && (
          <div className="container max-w-4xl mx-auto mt-3">
            <AlertPrimary type="danger" title={errorMessage} />
          </div>
        )}

        <div className="mt-5">
          <StyledLabel>
            {strings().form.address}
            <span className="text-primary">*</span>
          </StyledLabel>
          <StyledInput
            fullWidth
            className={` ${errors.address && `!border-red-500`} `}
            type="text"
            autoComplete="address-line1"
            {...register('address', { required: true })}
          />
          {errors.address && <StyledError>{strings().error.fieldIsRequired}</StyledError>}
        </div>

        <div className="mt-5">
          <StyledLabel>
            {strings().form.city}
            <span className="text-primary">*</span>
          </StyledLabel>
          <StyledInput
            fullWidth
            className={` ${errors.city && `!border-red-500`} `}
            type="text"
            autoComplete="address-level2"
            {...register('city', { required: true })}
          />
          {errors.city && <StyledError>{strings().error.fieldIsRequired}</StyledError>}
        </div>

        {region === 'us' && (
          <div className="mt-5">
            <StyledLabel>
              {strings().form.state || 'State'}
              <span className="text-primary">*</span>
            </StyledLabel>
            <StyledInput
              fullWidth
              className={` ${errors.state && `!border-red-500`} `}
              type="text"
              autoComplete="address-level1"
              {...register('state', { required: region === 'us' })}
            />
            {errors.state && <StyledError>{strings().error.fieldIsRequired}</StyledError>}
          </div>
        )}

        <div className="mt-5">
          <StyledLabel>
            {strings().form.zipCode}
            <span className="text-primary">*</span>
          </StyledLabel>
          <StyledInput
            fullWidth
            className={` ${errors.zipCode && `!border-red-500`} `}
            type="text"
            autoComplete="postal-code"
            {...register('zipCode', { required: true })}
          />
          {errors.zipCode && <StyledError>{strings().error.fieldIsRequired}</StyledError>}
        </div>

        <div className="mt-5">
          <StyledLabel>
            {strings().form.country}
            <span className="text-primary">*</span>
          </StyledLabel>
          <Controller
            name="countryId"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Select
                {...field}
                options={countries}
                placeholder={strings().form.country}
                getOptionValue={(option: Country) => option.id.toString()}
                getOptionLabel={(option: Country) => option.name}
                onChange={(option) => field.onChange(option?.id || null)}
                value={countries.find((c) => c.id === field.value) || null}
                className={errors.countryId ? 'border-red-500' : ''}
                styles={{
                  control: (base, state) => {
                    let borderColor = '#d1d5db';

                    if (errors.countryId) {
                      borderColor = '#ef4444';
                    } else if (state.isFocused) {
                      borderColor = '#6366f1';
                    }

                    return {
                      ...base,
                      borderColor,
                      boxShadow: state.isFocused ? '0 0 0 1px #6366f1' : base.boxShadow,
                      '&:hover': {
                        borderColor: state.isFocused ? '#6366f1' : '#d1d5db',
                      },
                    };
                  },
                }}
              />
            )}
          />
          {errors.countryId && <StyledError>{strings().error.fieldIsRequired}</StyledError>}
        </div>
        <div className="mt-5">
          <StyledLabel htmlFor="phone">
            {strings().form.phone}
            <span className="text-primary">*</span>
          </StyledLabel>
          <StyledInput
            fullWidth
            type="tel"
            id="phone"
            inputMode="tel"
            autoComplete="tel"
            className={` ${errors.phone && `!border-red-500`} `}
            {...register('phone', {
              required: !isSelfCo,
              pattern: {
                value: /^\+[1-9]\d{10,14}$/,
                message: strings().error.phone.invalid,
              },
            })}
          />
          {errors.phone && (
            <StyledError>
              {errors.phone.type === 'pattern' ? errors.phone.message : strings().error.phone.required}
            </StyledError>
          )}
        </div>
        <div className="flex justify-end">
          <CommonButtonDefault primary className="mt-5 md:mt-7" type="submit">
            {strings().button.save}
          </CommonButtonDefault>
        </div>
      </form>
    </div>
  );
}

UsersShipmentFormComponent.defaultProps = {
  isQuestionnaire: false,
  orderHash: '',
};

export default UsersShipmentFormComponent;
